/**
 * Secure redirect utility that validates URLs to prevent open redirect vulnerabilities
 */

import { ROUTES } from '@/routes/helpers/paths';
import { getAllowedOrigins } from './allowed-origins';

export const isValidRedirectUrl = (url: string): boolean => {
  try {
    // Handle relative URLs (without domain) - they're always valid for same-origin
    if (url.startsWith('/') && !url.startsWith('//')) {
      // Check if path matches any of the allowed routes
      return Object.values(ROUTES).some(route => {
        const routePattern = route.replace(/:\w+/g, '[^/]+');
        const routeRegex = new RegExp(`^${routePattern}(/.*)?$`);
        return routeRegex.test(url);
      });
    }

    // Handle absolute URLs
    const urlObj = new URL(url);
    const allowedOrigins = getAllowedOrigins();

    // Check if origin is allowed
    if (!allowedOrigins.includes(urlObj.origin)) {
      return false;
    }

    // Check if path matches any of the allowed routes
    const isAllowedPath = Object.values(ROUTES).some(route => {
      // Convert route pattern to regex for parameter matching
      const routePattern = route.replace(/:\w+/g, '[^/]+');
      const routeRegex = new RegExp(`^${routePattern}(/.*)?$`);
      return routeRegex.test(urlObj.pathname);
    });

    return isAllowedPath;
  } catch {
    return false;
  }
};

export const getSecureRedirectUrl = (
  redirectTo: string | null,
): string | null => {
  if (!redirectTo) return null;

  const isValid = isValidRedirectUrl(redirectTo);

  return isValid ? redirectTo : null;
};
